import { Col, Modal, Row, Form, Input, Select, Upload } from 'antd';
import * as React from 'react';
import 'react-quill/dist/quill.snow.css';
import { Provider, Subscribe } from 'unstated';
import { PlusOutlined } from '@ant-design/icons';

import { StateContainer } from './StateContainer';
import LanguageSelect from '../../../components/Others/LanguageSelect';
import { imageUrl } from '../../../config/config';

interface IBannerDetailProps {
  banner: GQL.IBanner;
  onSave: (banner: GQL.IBanner) => void;
  onCancel: () => void;
  languages: Array<GQL.ILanguage>;
}

export default class BannerDetail extends React.Component<IBannerDetailProps> {

  private bannerContainer: StateContainer;

  public constructor(props: IBannerDetailProps) {
    super(props);

    this.bannerContainer = new StateContainer({
      banner: props.banner,
      languages: props.languages
    });

    this.save = this.save.bind(this);
  }

  public render() {
    const { banner } = this.bannerContainer.state;
    const container = this.bannerContainer;

    return (
      <Provider>
        <Modal
          style={{ top: '5vh' }}
          width={'80%'}
          bodyStyle={{ minHeight: '80vh' }}
          title={
            this.props.banner == undefined || this.props.banner.id == undefined
              ? 'New banner'
              : `Banner ${banner.name}`
          }
          visible={true}
          cancelText={'Close'}
          okText={'Save changes'}
          onOk={this.save}
          onCancel={() => this.props.onCancel()}
        >
          <Subscribe to={[ container ]}>
            {bannerDetail => (
              <Row>
                <Col span={24}>
                  <Form>
                    <Row gutter={16}>
                      <Col span={4}>
                        <Form.Item
                          validateStatus={this.languageInputsStatus()}
                          hasFeedback={true}
                          label={'Current language'}
                        >
                          <LanguageSelect
                            name={'language'}
                            value={bannerDetail.state.currentLanguage}
                            onChange={(e: any, language: GQL.ILanguage) => container.setState({ currentLanguage: language.value })}
                            languages={this.props.languages}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          validateStatus={this.validateStatus('name')}
                          help={this.validateMessage('name')}
                          label={'Name'}
                          required={true}
                        >
                          <Input
                            name={'name'}
                            autoFocus={true}
                            addonBefore={'Internal'}
                            defaultValue={container.state.banner.name}
                            onChange={e => {
                              this.onChange('name', e.target.value);
                            }}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item
                          validateStatus={this.validateStatus('type')}
                          help={this.validateMessage('type')}
                          label={'Banner type'}
                          required={true}
                        >
                          <Select
                            autoFocus={true}
                            defaultValue={container.state.banner.type}
                            onChange={e => {
                              this.onChange('type', e);
                            }}
                          >
                            {[ 'HomePageHeader', 'HomePageCategory', 'TopMessage', 'PortfolioMessage' ].map((t: string) => (
                              <Select.Option key={t} value={t}>{t}</Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          validateStatus={this.validateStatus('file')}
                          help={this.validateMessage('file')}
                          label={'Image banner'}
                        >
                          <Upload
                            accept={'image/*'}
                            action={`${imageUrl}/images`}
                            listType="picture-card"
                            showUploadList={false}
                            onChange={({ file }: any) => {
                              if (file && file.response && file.response.file) {
                                this.onChange('file', file.response.file);
                              }
                            }}
                          >
                            <div>
                              {container.state.banner.file
                                ? <img src={imageUrl + '/static/' + container.state.banner.file} alt="Banner" style={{ maxWidth: '320px' }} />
                                : <React.Fragment>
                                    <PlusOutlined />
                                    <div className="ant-upload-text">Upload</div>
                                  </React.Fragment>}
                            </div>
                          </Upload>
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          validateStatus={this.validateStatus('productImage')}
                          help={this.validateMessage('productImage')}
                          label={'Product image'}
                        >
                          <Upload
                            accept={'image/*'}
                            action={`${imageUrl}/images`}
                            listType="picture-card"
                            showUploadList={false}
                            onChange={({ file }: any) => {
                              if (file && file.response && file.response.file) {
                                this.onChange('productImage', file.response.file);
                              }
                            }}
                          >
                            <div>
                              {container.state.banner.productImage
                                ? <img src={imageUrl + '/static/' + container.state.banner.productImage} alt="Banner" style={{ maxWidth: '320px' }} />
                                : <React.Fragment>
                                    <PlusOutlined />
                                    <div className="ant-upload-text">Upload</div>
                                  </React.Fragment>}
                            </div>
                          </Upload>
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          validateStatus={this.validateStatus('title')}
                          help={this.validateMessage('title')}
                          label={'Title - ' + container.state.currentLanguage}
                        >
                          <Input
                            key={container.state.currentLanguage}
                            name={'title'}
                            addonBefore={container.state.currentLanguage}
                            defaultValue={container.state.banner.title[ container.state.currentLanguage ]}
                            onChange={e => {
                              this.onChange('title', e.target.value, false, container.state.currentLanguage);
                            }}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          validateStatus={this.validateStatus('subtitle')}
                          help={this.validateMessage('subtitle')}
                          label={'Subtitle - ' + container.state.currentLanguage}
                        >
                          <Input
                            key={container.state.currentLanguage}
                            name={'subtitle'}
                            addonBefore={container.state.currentLanguage}
                            defaultValue={container.state.banner.subtitle[ container.state.currentLanguage ]}
                            onChange={e => {
                              this.onChange('subtitle', e.target.value, false, container.state.currentLanguage);
                            }}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          validateStatus={this.validateStatus('description')}
                          help={this.validateMessage('description')}
                          label={'Description - ' + container.state.currentLanguage}
                        >
                          <Input
                            key={container.state.currentLanguage}
                            name={'description'}
                            addonBefore={container.state.currentLanguage}
                            defaultValue={container.state.banner.description[ container.state.currentLanguage ]}
                            onChange={e => {
                              this.onChange('description', e.target.value, false, container.state.currentLanguage);
                            }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={16}>
                      <Col span={20}>
                        <Form.Item
                          validateStatus={this.validateStatus('link')}
                          help={this.validateMessage('link')}
                          label={'Link'}
                        >
                          <Input
                            key={'link'}
                            name={'link'}
                            // addonBefore={container.state.currentLanguage}
                            defaultValue={container.state.banner.link || ''}
                            onChange={e => {
                              this.onChange('link', e.target.value);
                            }}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item
                          validateStatus={this.validateStatus('priority')}
                          help={this.validateMessage('priority')}
                          label={'Priority'}
                        >
                          <Input
                            key={'priority'}
                            name={'priority'}
                            // addonBefore={null}
                            defaultValue={container.state.banner.priority ? container.state.banner.priority.toString() : ''}
                            onChange={e => {
                              this.onChange('priority', parseInt(e.target.value));
                            }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Form>
                </Col>
              </Row>
            )}
          </Subscribe>
        </Modal>
      </Provider>
    );
  }

  private onChange(key: string, val: any, validate?: boolean, language?: string) {
    this.bannerContainer.onChange(key, val, validate, language);
  }

  private validateMessage(field: string) {
    const error = this.bannerContainer.state.errors[ field ];
    const warning = this.bannerContainer.state.warnings[ field ];

    if (error) {
      return error;
    }

    return warning;
  }

  private validateStatus(field: string) {
    const error = this.bannerContainer.state.errors[ field ];
    const warning = this.bannerContainer.state.warnings[ field ];

    if (error) {
      return 'error';
    }
    if (warning) {
      return 'warning';
    }

    return undefined;
  }

  private languageInputsStatus() {
    let valid = true;
    for (const key of Object.keys(this.bannerContainer.state.warnings)) {
      if (this.bannerContainer.state.warnings[ key ] != undefined) {
        // console.log(`returning false cause ${key} is not valid`, {
        //     stuff: this.props.container.state.warnings.generalSettings
        // });
        valid = false;
        break;
      }
    }

    if (valid) {
      return undefined;
    }

    return 'warning';
  }

  private async save() {
    const errors = await this.bannerContainer.validate(); // TODO
    let anyError = false;

    for (const key of Object.keys(errors)) {
      if (errors[ key ] != undefined) {
        anyError = true;
      }
    }

    if (!anyError) {
      this.props.onSave(this.bannerContainer.state.banner);
    } else {
      console.log('there is error, can not save', errors);
    }
  }
}



// WEBPACK FOOTER //
// ./src/scenes/Banners/BannersDetail/BannerDetail.tsx